import { getFieldConfig } from './searchEntityFieldConfigs.js';

const SEARCH_HIGHLIGHT_DEBUG = process.env.NODE_ENV === 'development';

const safeStringify = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return JSON.stringify(
      { error: 'Unable to stringify payload', message: error.message },
      null,
      2
    );
  }
};

const logSearchHighlight = (message, payload) => {
  if (!SEARCH_HIGHLIGHT_DEBUG) return;
  console.log(`[searchHighlight] ${message}\n${safeStringify(payload)}`);
};

const ACCENT_MAP = {
  a: '[aàáâãäå]',
  e: '[eéèêë]',
  i: '[iíìîï]',
  o: '[oóòôõö]',
  u: '[uúùûü]',
  c: '[cç]',
  n: '[nñ]',
  y: '[yýÿ]',
};

const escapeRegex = (str) => String(str).replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

const accentInsensitivePattern = (str) =>
  [...String(str)].map((ch) => ACCENT_MAP[ch.toLowerCase()] || escapeRegex(ch)).join('');

const normalizeForMatch = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const MAX_HIGHLIGHT_TEXT_LENGTH = 4096;
const FUZZY_MIN_TERM_LENGTH = 3;

const NAME_SPLIT_CONFIGS = {
  missionList: {
    firstnameId: 'candidate_firstname',
    lastnameId: 'candidate_lastname',
  },
  missionCommandPalette: {
    firstnameId: 'candidate_firstname',
    lastnameId: 'candidate_lastname',
  },
  tempWorker: {
    firstnameId: 'firstname',
    lastnameId: 'lastname',
  },
  clientContact: {
    firstnameId: 'firstname',
    lastnameId: 'lastname',
  },
};

const NAME_SPLIT_CONTEXTS = new Set(Object.keys(NAME_SPLIT_CONFIGS));

const buildNormalizedIndexMap = (text) => {
  let normalized = '';
  const indexMap = [];

  for (let i = 0; i < text.length; i += 1) {
    const normalizedChar = normalizeForMatch(text[i]);
    for (let j = 0; j < normalizedChar.length; j += 1) {
      normalized += normalizedChar[j];
      indexMap.push(i);
    }
  }

  return { normalized, indexMap };
};

const mapNormalizedRangeToOriginal = (indexMap, start, length) => {
  if (length <= 0 || start >= indexMap.length) return null;
  const endIndex = start + length - 1;
  if (endIndex >= indexMap.length) return null;
  return {
    start: indexMap[start],
    end: indexMap[endIndex] + 1,
  };
};

const findSubstringHighlightRanges = (text, term) => {
  const normalizedTerm = normalizeForMatch(term);
  if (!normalizedTerm) return [];

  const { normalized, indexMap } = buildNormalizedIndexMap(text);
  const ranges = [];
  let fromIndex = 0;

  while (fromIndex <= normalized.length - normalizedTerm.length) {
    const matchIndex = normalized.indexOf(normalizedTerm, fromIndex);
    if (matchIndex === -1) break;

    const range = mapNormalizedRangeToOriginal(
      indexMap,
      matchIndex,
      normalizedTerm.length
    );
    if (range) ranges.push(range);

    fromIndex = matchIndex + 1;
  }

  return ranges;
};

const findTokenHighlightRanges = (text, term, { fuzzy = false } = {}) => {
  const ranges = [];
  const tokenPattern = /[\p{L}\p{N}@.+_-]+/gu;
  let match = tokenPattern.exec(text);
  let guard = 0;

  while (match && guard++ < text.length + 1) {
    const token = match[0];
    if (
      fieldMatchesSearch(token, term, { type: 'text', fuzzy: false }) ||
      fieldMatchesSearch(token, term, { type: 'text', fuzzy: true })
    ) {
      ranges.push({ start: match.index, end: match.index + token.length });
    }
    match = tokenPattern.exec(text);
  }

  return ranges;
};

const findPrefixHighlightRanges = (text, prefix) => {
  const normalizedPrefix = normalizeForMatch(prefix);
  if (!normalizedPrefix) return [];

  const ranges = [];
  const tokenPattern = /[\p{L}\p{N}@.+_-]+/gu;
  let match = tokenPattern.exec(text);
  let guard = 0;

  while (match && guard++ < text.length + 1) {
    if (normalizeForMatch(match[0]).startsWith(normalizedPrefix)) {
      ranges.push({ start: match.index, end: match.index + match[0].length });
    }
    match = tokenPattern.exec(text);
  }

  return ranges;
};

const findPhoneHighlightRangesByDigitIndex = (text, variant) => {
  if (variant.length < 4) return null;

  const digitIndexes = [];
  for (let i = 0; i < text.length; i += 1) {
    if (/\d/.test(text[i])) digitIndexes.push(i);
  }

  if (digitIndexes.length < variant.length) return null;

  const allDigits = digitIndexes.map((index) => text[index]).join('');
  const matchIndex = allDigits.indexOf(variant);
  if (matchIndex === -1) return null;

  return {
    start: digitIndexes[matchIndex],
    end: digitIndexes[matchIndex + variant.length - 1] + 1,
  };
};

const findPhoneHighlightRanges = (text, term) => {
  const stringText = String(text || '');
  const variants = getPhoneDigitVariants(term).sort((a, b) => b.length - a.length);

  for (const variant of variants) {
    const digitRange = findPhoneHighlightRangesByDigitIndex(stringText, variant);
    if (digitRange) return [digitRange];

    try {
      const regex = buildFlexibleDigitsRegex(variant);
      const match = regex.exec(stringText);
      if (match?.[0]) {
        return [{ start: match.index, end: match.index + match[0].length }];
      }
    } catch {
      // ignore invalid regex
    }
  }

  return [];
};

const isPhoneLikeTerm = (term) => {
  const trimmed = String(term || '').trim();
  const termDigits = digitsOnly(trimmed);
  return termDigits.length >= 4 && termDigits.length === trimmed.replace(/[\s.()-]/g, '').length;
};

const mergeHighlightRanges = (ranges) => {
  if (ranges.length === 0) return [];

  const sorted = [...ranges].sort(
    (a, b) => a.start - b.start || b.end - a.end - (a.end - a.start)
  );
  const merged = [];

  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (!last || range.start >= last.end) {
      merged.push({ ...range });
    } else if (range.end > last.end) {
      last.end = range.end;
    }
  }

  return merged;
};

const mergeAdjacentHighlightRanges = (ranges, text) => {
  const merged = mergeHighlightRanges(ranges);
  if (!text || merged.length <= 1) return merged;

  const combined = [{ ...merged[0] }];

  for (let index = 1; index < merged.length; index += 1) {
    const previous = combined[combined.length - 1];
    const current = merged[index];
    const gap = text.slice(previous.end, current.start);

    if (/^[\s\-–—,./]*$/.test(gap)) {
      previous.end = current.end;
      continue;
    }

    combined.push({ ...current });
  }

  return combined;
};

const buildFlexibleDigitsRegex = (digits) =>
  new RegExp(digits.split('').join('\\D*'), 'i');

const getPhoneDigitVariants = (term) => {
  const digits = digitsOnly(term);
  if (digits.length < 4) return [];

  const variants = new Set([digits]);

  if (digits.startsWith('0')) {
    variants.add(digits.slice(1));
  }

  if (digits.startsWith('596')) {
    const nationalPart = digits.slice(3);
    variants.add(nationalPart);
    variants.add(nationalPart.startsWith('0') ? nationalPart : `0${nationalPart}`);
  }

  if (digits.startsWith('33')) {
    const nationalPart = digits.slice(2);
    variants.add(nationalPart);
    variants.add(nationalPart.startsWith('0') ? nationalPart : `0${nationalPart}`);
  }

  return [...variants];
};

const phoneValuesMatch = (value, term) => {
  const stringValue = String(value || '');
  const valueDigits = digitsOnly(stringValue);
  const variants = getPhoneDigitVariants(term);

  for (const variant of variants) {
    if (variant.length < 4) continue;
    if (valueDigits.includes(variant) || variant.includes(valueDigits)) return true;
    try {
      if (buildFlexibleDigitsRegex(variant).test(stringValue)) return true;
    } catch {
      // ignore invalid regex
    }
  }

  return false;
};

const damerauLevenshteinDistance = (left, right) => {
  const a = normalizeForMatch(left);
  const b = normalizeForMatch(right);
  const alen = a.length;
  const blen = b.length;

  if (alen === 0) return blen;
  if (blen === 0) return alen;

  const matrix = Array.from({ length: alen + 1 }, () => Array(blen + 1).fill(0));

  for (let i = 0; i <= alen; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= blen; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= alen; i += 1) {
    for (let j = 1; j <= blen; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + cost);
      }
    }
  }

  return matrix[alen][blen];
};

const wordMatchesTermWithTolerance = (word, term, maxDistance = 1) => {
  const normalizedWord = normalizeForMatch(word);
  const normalizedTerm = normalizeForMatch(term);
  if (!normalizedTerm) return false;
  if (normalizedWord.includes(normalizedTerm)) return true;
  if (normalizedTerm.length < FUZZY_MIN_TERM_LENGTH) return false;
  return damerauLevenshteinDistance(normalizedWord, normalizedTerm) <= maxDistance;
};

const textMatchesTermWithTolerance = (value, term, maxDistance = 1) => {
  const normalizedValue = normalizeForMatch(value);
  const normalizedTerm = normalizeForMatch(term);
  if (!normalizedTerm) return false;
  if (normalizedValue.includes(normalizedTerm)) return true;
  if (normalizedTerm.length < FUZZY_MIN_TERM_LENGTH) return false;

  const words = normalizedValue.split(/[\s-]+/).filter(Boolean);
  if (words.some((word) => wordMatchesTermWithTolerance(word, term, maxDistance))) {
    return true;
  }

  return damerauLevenshteinDistance(normalizedValue, normalizedTerm) <= maxDistance;
};

const isExactSubstringMatch = (text, term) => {
  const normalizedValue = normalizeForMatch(text);
  const normalizedTerm = normalizeForMatch(term);
  return Boolean(normalizedTerm) && normalizedValue.includes(normalizedTerm);
};

const isFuzzyOnlyMatch = (text, term) =>
  !isExactSubstringMatch(text, term) &&
  textMatchesTermWithTolerance(text, term, 1);

const extractHighlightTerms = (search) => {
  const raw = String(search || '').trim();
  if (!raw) return [];

  const terms = [];
  let pos = 0;

  while (pos < raw.length) {
    if (/\s/.test(raw[pos])) {
      pos += 1;
      continue;
    }

    if (raw[pos] === '"') {
      const close = raw.indexOf('"', pos + 1);
      if (close !== -1) {
        const phrase = raw.slice(pos + 1, close).trim();
        if (phrase) terms.push(phrase);
        pos = close + 1;
        continue;
      }
      pos += 1;
      continue;
    }

    if ('()'.includes(raw[pos])) {
      pos += 1;
      continue;
    }

    const wordMatch = raw.slice(pos).match(/^[^\s()"]+/);
    if (!wordMatch) {
      pos += 1;
      continue;
    }

    const word = wordMatch[0];
    if (['ET', 'OU', 'NON'].includes(word)) {
      pos += word.length;
      continue;
    }

    if (word.endsWith('*') && word.length > 1) {
      terms.push(word.slice(0, -1));
    } else {
      const fuzzyMatch = word.match(/^(.+)~\d+$/);
      terms.push(fuzzyMatch ? fuzzyMatch[1] : word);
    }

    pos += word.length;
  }

  return [...new Set(terms.filter(Boolean))];
};

const fieldMatchesSearch = (value, term, { type = 'text', fuzzy = false, multiTerm = false } = {}) => {
  if (value === null || value === undefined || value === '') return false;
  const stringValue = String(value);
  const normalizedValue = normalizeForMatch(stringValue);
  const normalizedTerm = normalizeForMatch(term);

  if (!normalizedTerm) return false;

  if (type === 'phone') {
    return phoneValuesMatch(stringValue, term);
  }

  if (type === 'number') {
    const termDigits = digitsOnly(term);
    if (termDigits.length >= 2 && digitsOnly(stringValue).includes(termDigits)) return true;
    if (normalizedValue.includes(normalizedTerm)) return true;
    return String(value) === String(term);
  }

  if (normalizedValue.includes(normalizedTerm)) return true;

  if (term.endsWith('*') || normalizedTerm.length >= 2) {
    const prefix = term.endsWith('*') ? term.slice(0, -1) : normalizedTerm;
    const prefixPattern = new RegExp(`^${accentInsensitivePattern(prefix)}`, 'i');
    if (prefixPattern.test(stringValue)) return true;
  }

  const allowFuzzy = fuzzy && normalizedTerm.length >= FUZZY_MIN_TERM_LENGTH;

  if (allowFuzzy && textMatchesTermWithTolerance(stringValue, term, 1)) {
    return true;
  }

  return false;
};

const termMatchesFieldValue = (rawValue, term, field, multiTerm = false) =>
  fieldMatchesSearch(rawValue, term, {
    type: field.type,
    fuzzy: field.fuzzy,
    multiTerm,
  });

const getPersonNameSplitMatch = (item, fields, terms, context) => {
  const config = NAME_SPLIT_CONFIGS[context];
  if (!config || terms.length !== 2) return null;

  const firstnameField = fields.find((field) => field.id === config.firstnameId);
  const lastnameField = fields.find((field) => field.id === config.lastnameId);
  if (!firstnameField || !lastnameField) return null;

  const firstname = firstnameField.getValue(item);
  const lastname = lastnameField.getValue(item);
  if (!firstname && !lastname) return null;

  const orientations = [
    { firstTerm: terms[0], lastTerm: terms[1] },
    { firstTerm: terms[1], lastTerm: terms[0] },
  ];

  for (const { firstTerm, lastTerm } of orientations) {
    if (
      termMatchesFieldValue(firstname, firstTerm, firstnameField, true) &&
      termMatchesFieldValue(lastname, lastTerm, lastnameField, true)
    ) {
      return {
        firstnameField,
        lastnameField,
        firstname,
        lastname,
        firstTerm,
        lastTerm,
        firstnameId: config.firstnameId,
        lastnameId: config.lastnameId,
      };
    }
  }

  return null;
};

const findHighlightRanges = (text, terms, { fuzzy = false } = {}) => {
  if (!text || !terms?.length) return [];

  const stringText = String(text);
  if (stringText.length > MAX_HIGHLIGHT_TEXT_LENGTH) return [];

  const ranges = [];

  for (const term of [...terms].sort((a, b) => b.length - a.length)) {
    if (!term) continue;

    if (isPhoneLikeTerm(term)) {
      ranges.push(...findPhoneHighlightRanges(stringText, term));
      continue;
    }

    const exactMatch = isExactSubstringMatch(stringText, term);
    const fuzzyOnlyMatch = isFuzzyOnlyMatch(stringText, term);
    const isSingleUnitText = !/\s/.test(stringText.trim());

    if (fuzzyOnlyMatch && isSingleUnitText) {
      ranges.push({ start: 0, end: stringText.length });
      continue;
    }

    if (exactMatch) {
      ranges.push(...findSubstringHighlightRanges(stringText, term));
      continue;
    }

    if (fuzzyOnlyMatch) {
      ranges.push(...findTokenHighlightRanges(stringText, term, { fuzzy: true }));
      continue;
    }

    ranges.push(...findSubstringHighlightRanges(stringText, term));
    ranges.push(...findTokenHighlightRanges(stringText, term, { fuzzy }));

    if (term.endsWith('*')) {
      const prefix = term.slice(0, -1);
      if (prefix) {
        ranges.push(...findPrefixHighlightRanges(stringText, prefix));
      }
    }
  }

  return mergeAdjacentHighlightRanges(ranges, stringText);
};

const formatMatchHint = (labelFr, value) => {
  const displayValue = String(value || '').trim();
  if (!displayValue) return `Trouvé "${labelFr}"`;
  return `Trouvé "${labelFr} ${displayValue}"`;
};

const formatFieldDisplayValue = (field, rawValue) => {
  if (field.type === 'number' && rawValue !== null && rawValue !== undefined && rawValue !== '') {
    return String(rawValue);
  }
  return String(rawValue || '').trim();
};

const fieldValuesOverlap = (left, right) => {
  const normalizedLeft = normalizeForMatch(left);
  const normalizedRight = normalizeForMatch(right);
  if (!normalizedLeft || !normalizedRight) return false;
  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.includes(normalizedRight) ||
    normalizedRight.includes(normalizedLeft)
  );
};

const isHiddenMatchAlreadyVisible = (displayValue, matchedTerms, matchedVisibleFields) =>
  matchedVisibleFields.some(
    ({ value, terms }) =>
      matchedTerms.some((term) => terms.includes(term)) &&
      fieldValuesOverlap(value, displayValue)
  );

const pushHiddenFieldHint = (
  matchHints,
  field,
  displayValue,
  matchedTerms,
  matchedVisibleFields
) => {
  if (isHiddenMatchAlreadyVisible(displayValue, matchedTerms, matchedVisibleFields)) {
    return;
  }
  matchHints.push(formatMatchHint(field.labelFr, displayValue));
};

const buildMatchPresentation = ({ search, item, context, includeCvHeuristic = false }) => {
  const terms = extractHighlightTerms(search);
  const emptySlots = {};
  const itemLabel =
    item?.id_mission ||
    item?.reference ||
    item?._id ||
    item?.id_user ||
    item?.name_commercial ||
    'unknown';

  if (terms.length === 0 || String(search || '').trim().length < 2) {
    logSearchHighlight('skip: search too short', { context, search, itemLabel });
    return { matchHints: [], slotTerms: emptySlots, terms: [] };
  }

  const fields = getFieldConfig(context);
  const slotTerms = {};
  const matchHints = [];
  const matchedFields = [];
  const matchedVisibleFields = [];
  let hasNonCvMatch = false;
  const multiTerm = terms.length > 1;
  const useNameSplit = NAME_SPLIT_CONTEXTS.has(context) && terms.length === 2;
  const nameSplitMatch = useNameSplit ? getPersonNameSplitMatch(item, fields, terms, context) : null;
  const nameSplitFieldIds = nameSplitMatch
    ? new Set([nameSplitMatch.firstnameId, nameSplitMatch.lastnameId])
    : null;
  const nameSplitVisibleSlot = nameSplitMatch?.firstnameField?.visibleIn || null;

  for (const field of fields) {
    if (field.cvOnly) continue;

    const rawValue = field.getValue(item);
    if (rawValue === null || rawValue === undefined || rawValue === '') continue;

    if (
      nameSplitMatch &&
      field.visibleIn &&
      field.visibleIn !== nameSplitVisibleSlot &&
      !nameSplitFieldIds.has(field.id)
    ) {
      continue;
    }

    if (useNameSplit && nameSplitFieldIds?.has(field.id)) {
      if (!nameSplitMatch) continue;

      const matchedTerm =
        field.id === nameSplitMatch.firstnameId
          ? nameSplitMatch.firstTerm
          : nameSplitMatch.lastTerm;

      if (!termMatchesFieldValue(rawValue, matchedTerm, field, true)) continue;

      hasNonCvMatch = true;
      const displayValue = formatFieldDisplayValue(field, rawValue);

      matchedFields.push({
        id: field.id,
        labelFr: field.labelFr,
        rawValue: displayValue,
        matchedTerms: [matchedTerm],
        visibleIn: field.visibleIn || null,
      });

      if (field.visibleIn) {
        if (!slotTerms[field.visibleIn]) slotTerms[field.visibleIn] = [];
        slotTerms[field.visibleIn].push(matchedTerm);
        matchedVisibleFields.push({ value: displayValue, terms: [matchedTerm] });
      } else {
        pushHiddenFieldHint(
          matchHints,
          field,
          displayValue,
          [matchedTerm],
          matchedVisibleFields
        );
      }
      continue;
    }

    const matchedTerms = terms.filter((term) =>
      fieldMatchesSearch(rawValue, term, {
        type: field.type,
        fuzzy: field.fuzzy,
        multiTerm,
      })
    );

    if (matchedTerms.length === 0) continue;

    hasNonCvMatch = true;
    const displayValue = formatFieldDisplayValue(field, rawValue);

    matchedFields.push({
      id: field.id,
      labelFr: field.labelFr,
      rawValue: displayValue,
      matchedTerms,
      visibleIn: field.visibleIn || null,
    });

    if (field.visibleIn) {
      if (!slotTerms[field.visibleIn]) slotTerms[field.visibleIn] = [];
      slotTerms[field.visibleIn].push(...matchedTerms);
      matchedVisibleFields.push({ value: displayValue, terms: matchedTerms });
    } else {
      pushHiddenFieldHint(
        matchHints,
        field,
        displayValue,
        matchedTerms,
        matchedVisibleFields
      );
    }
  }

  if (includeCvHeuristic && !hasNonCvMatch) {
    const cvTerms = terms.filter(
      (term) => !term.includes('@') && digitsOnly(term).length < 4
    );
    if (cvTerms.length > 0) {
      const cvHint = formatMatchHint('CV', cvTerms[0]);
      matchHints.push(cvHint);
      matchedFields.push({
        id: 'cv',
        labelFr: 'CV',
        rawValue: cvTerms[0],
        matchedTerms: cvTerms,
        visibleIn: null,
        heuristic: true,
      });
    }
  }

  Object.keys(slotTerms).forEach((slot) => {
    slotTerms[slot] = [...new Set(slotTerms[slot])];
  });

  const hasVisibleHighlights = Object.keys(slotTerms).length > 0;

  const presentation = {
    matchHints: hasVisibleHighlights ? [] : [...new Set(matchHints)],
    slotTerms,
    terms,
  };

  logSearchHighlight('match result', {
    context,
    search,
    terms,
    itemLabel,
    matchedFields,
    matchHints: presentation.matchHints,
    slotTerms: presentation.slotTerms,
    willShowTrouveLines: presentation.matchHints.length > 0,
    willShowBold: Object.keys(presentation.slotTerms).length > 0,
  });

  if (matchedFields.length === 0) {
    logSearchHighlight('no client-side match for result item', {
      context,
      search,
      terms,
      itemLabel,
      itemSample: item,
    });
  }

  return presentation;
};

export {
  extractHighlightTerms,
  normalizeForMatch,
  fieldMatchesSearch,
  findHighlightRanges,
  buildMatchPresentation,
  formatMatchHint,
};
